var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useEffect, useRef, useState } from 'react';
import { getStudyById, initiateStudy } from '../api';
const INITIAL_STUDY_PAYLOAD = {
    name: '',
    mediaType: '',
    goal: '',
    audiences: [],
    minAge: 0,
    maxAge: 0,
    mediaUri: '',
    thumbnailUri: '',
};
export const useStudy = () => {
    const [studyPayload, setStudyPayload] = useState(INITIAL_STUDY_PAYLOAD);
    const [studyResultData, setStudyResultData] = useState(null);
    const [studyResultDataLoading, setStudyResultDataLoading] = useState(false);
    const [studyResultDataError, setStudyResultDataError] = useState('');
    const [canCreateStudy, setCanCreateStudy] = useState(true);
    const [payloadError, setPayloadError] = useState(false);
    const timerRef = useRef(null);
    const resetStudyData = () => {
        if (timerRef.current)
            clearTimeout(timerRef.current);
        setPayloadError(false);
        setCanCreateStudy(true);
        setStudyResultData(null);
        setStudyResultDataLoading(false);
        setStudyResultDataError('');
        setStudyPayload(INITIAL_STUDY_PAYLOAD);
    };
    useEffect(() => {
        if (studyResultData) {
            setStudyPayload(prev => (Object.assign(Object.assign({}, prev), { name: studyResultData.name || '', mediaType: studyResultData.mediaType || '', goal: studyResultData.goal || '', audiences: studyResultData.audiences || [], minAge: studyResultData.minAge || 0, maxAge: studyResultData.maxAge || 0 })));
        }
    }, [studyResultData]);
    useEffect(() => {
        if (!(studyResultData === null || studyResultData === void 0 ? void 0 : studyResultData.id))
            return;
        if (studyResultData.result !== null) {
            if (timerRef.current)
                clearTimeout(timerRef.current);
            return;
        }
        const POLL_MS = 1000;
        const poll = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const { data } = yield getStudyById(studyResultData.id);
                setStudyResultData(prev => (prev ? Object.assign(Object.assign({}, prev), data) : data));
                if (data.result === null) {
                    timerRef.current = setTimeout(poll, POLL_MS);
                }
            }
            catch (err) {
                console.error('Polling error:', err);
                timerRef.current = setTimeout(poll, POLL_MS); // retry on error
            }
        });
        timerRef.current = setTimeout(poll, POLL_MS);
        return () => {
            if (timerRef.current)
                clearTimeout(timerRef.current);
        };
    }, [studyResultData === null || studyResultData === void 0 ? void 0 : studyResultData.id, studyResultData === null || studyResultData === void 0 ? void 0 : studyResultData.result]);
    const loadStudyById = (id) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setCanCreateStudy(false);
            setStudyResultDataLoading(true);
            setStudyResultDataError('');
            const { data: completedStudyData } = yield getStudyById(id);
            setStudyResultData(prev => (Object.assign(Object.assign({}, prev), completedStudyData)));
        }
        catch (error) {
            console.log('Error creative study:', error);
            setStudyResultDataError('Failed to fetch the creative study. Please try again.');
        }
        finally {
            setStudyResultDataLoading(false);
        }
    });
    const validatePayload = () => {
        const { mediaType, goal, audiences, minAge, maxAge, mediaUri } = studyPayload;
        if (!mediaType || !goal || audiences.length === 0 || minAge < 0 || maxAge < 0 || !mediaUri) {
            setPayloadError(true);
            return false;
        }
        if (minAge >= maxAge) {
            setPayloadError(true);
            return false;
        }
        setPayloadError(false);
        return true;
    };
    const processStudy = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setPayloadError(false);
            if (!validatePayload()) {
                return;
            }
            setCanCreateStudy(false);
            setStudyResultDataLoading(true);
            setStudyResultDataError('');
            const { data: initiatedStudyData } = yield initiateStudy(studyPayload);
            setStudyResultData(initiatedStudyData);
            const { data: completedStudyData } = yield getStudyById(initiatedStudyData.id);
            setStudyResultData(prev => (Object.assign(Object.assign({}, prev), completedStudyData)));
        }
        catch (error) {
            setCanCreateStudy(false);
            console.log('Error creative study:', error);
            setStudyResultDataError('Failed to create the creative study. Please try again.');
        }
        finally {
            setStudyResultDataLoading(false);
        }
    });
    const updateGoalPayload = (newGoal) => {
        setStudyPayload(prev => (Object.assign(Object.assign({}, prev), { goal: newGoal })));
    };
    const updateMediaTypePayload = (newMediaType) => {
        setStudyPayload(prev => (Object.assign(Object.assign({}, prev), { mediaType: newMediaType })));
    };
    const updateAudiencesPayload = (newAudiences) => {
        setStudyPayload(prev => (Object.assign(Object.assign({}, prev), { audiences: newAudiences })));
    };
    const updateAgeRangePayload = (newMinAge, newMaxAge) => {
        setStudyPayload(prev => (Object.assign(Object.assign({}, prev), { minAge: newMinAge, maxAge: newMaxAge })));
    };
    const updateMediaUriPayload = (newMediaUri) => {
        setStudyPayload(prev => (Object.assign(Object.assign({}, prev), { mediaUri: newMediaUri })));
    };
    return {
        studyPayload,
        studyResultData,
        processStudy,
        studyResultDataError,
        studyResultDataLoading,
        updateGoalPayload,
        updateMediaTypePayload,
        updateAudiencesPayload,
        updateAgeRangePayload,
        updateMediaUriPayload,
        resetStudyData,
        loadStudyById,
        canCreateStudy,
        payloadError
    };
};
